import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import {
  ProductCatalogApiProvider,
  useProductCatalogApi,
} from '../Services/ProductCatalogApiProvider';
import { formatDateUTC } from '../utils/formatDate';

function ProductCatalogs({ canEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getCatalogs, deleteCatalog } = useProductCatalogApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [totalCatalogs, setTotalCatalogs] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: false },
    { name: 'Name', selector: (row) => row.name, sortable: false },
    {
      name: 'Status',
      selector: (row) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: false },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: false },
  ];

  function onDelete(selectedId) {
    if (!selectedId) return;

    showConfirmation(`Are you sure you want to delete the selected item?`, async () => {
      try {
        await deleteCatalog(selectedId);
        showAlert('success', 'Item was successfully deleted!');
        featchCatalogs(0, itemsPerPage);
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      }
    });
  }

  function onOpen(row) {
    if (!row) return;
    navigate(`/organizations/${organizationId}/product-catalogs/${row.id}`, {
      state: { organizationId },
    });
  }

  const featchCatalogs = async (skip, limit) => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    try {
      setLoading(true);
      const response = await getCatalogs(skip, limit);
      setCatalogs(response.items);
      setTotalCatalogs(response.totalCount);
    } catch (error) {
      setError(error.message || 'Failed to fetch catalogs!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    featchCatalogs(0, itemsPerPage);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Product Catalogs"
                columns={columns}
                data={catalogs}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={itemsPerPage}
                paginationTotalRows={totalCatalogs}
                onRowClicked={onOpen}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={onDelete}
                onChangePage={featchCatalogs}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(
                      `/organizations/${organizationId}/product-catalogs/new-product-catalog`,
                    )
                  }
                >
                  Create New Product Catalog
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductCatalogsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <ProductCatalogApiProvider>
        <ProductCatalogs canEdit={canEdit} />
      </ProductCatalogApiProvider>
    </ConfirmationProvider>
  );
}

export default ProductCatalogsWrapper;
